/**
 * Naval Battle art pack — vendored per the OSS-assets-first rule (memory/oss-assets-first).
 * Art by Molly "Cougarmint" Willits, CC-BY 3.0 (see the credit in docs/CREDITS.md and the
 * pack's readme). Sliced from the original sheets into per-sprite PNGs at import time
 * (scripted with PIL, 2026-08-04) so rendering is plain `<img>`/background URLs with no
 * CSS-sprite offset math to get wrong.
 *
 * Visual language: **your fleet sails the blue ocean; your shots land on the green radar.**
 */
import gridOceanField from './assets/naval/grid-ocean-field.png';
import gridRadarField from './assets/naval/grid-radar-field.png';
import tokenExplosion from './assets/naval/token-explosion.png';
import tokenSplash from './assets/naval/token-splash.png';
import shipV2 from './assets/naval/ship-v-2.png';
import shipV3a from './assets/naval/ship-v-3a.png';
import shipV3b from './assets/naval/ship-v-3b.png';
import shipV4 from './assets/naval/ship-v-4.png';
import shipV5 from './assets/naval/ship-v-5.png';
import shipV2Hit from './assets/naval/ship-v-hit-2.png';
import shipV3aHit from './assets/naval/ship-v-hit-3a.png';
import shipV3bHit from './assets/naval/ship-v-hit-3b.png';
import shipV4Hit from './assets/naval/ship-v-hit-4.png';
import shipV5Hit from './assets/naval/ship-v-hit-5.png';
import shipH2 from './assets/naval/ship-h-2.png';
import shipH3a from './assets/naval/ship-h-3a.png';
import shipH3b from './assets/naval/ship-h-3b.png';
import shipH4 from './assets/naval/ship-h-4.png';
import shipH5 from './assets/naval/ship-h-5.png';
import shipH2Hit from './assets/naval/ship-h-hit-2.png';
import shipH3aHit from './assets/naval/ship-h-hit-3a.png';
import shipH3bHit from './assets/naval/ship-h-hit-3b.png';
import shipH4Hit from './assets/naval/ship-h-hit-4.png';
import shipH5Hit from './assets/naval/ship-h-hit-5.png';

export type GridSkin = 'ocean' | 'radar';

export const GRID_FIELDS: Record<GridSkin, string> = {
  ocean: gridOceanField,
  radar: gridRadarField,
};

export const TOKENS = { hit: tokenExplosion, miss: tokenSplash } as const;

/** Sprite key by ship length; the two 3-long ships get distinct hulls (sub vs cruiser). */
type SpriteKey = '2' | '3a' | '3b' | '4' | '5';

const SPRITES: Record<'v' | 'h', Record<'ok' | 'hit', Record<SpriteKey, string>>> = {
  v: {
    ok: { '2': shipV2, '3a': shipV3a, '3b': shipV3b, '4': shipV4, '5': shipV5 },
    hit: { '2': shipV2Hit, '3a': shipV3aHit, '3b': shipV3bHit, '4': shipV4Hit, '5': shipV5Hit },
  },
  h: {
    ok: { '2': shipH2, '3a': shipH3a, '3b': shipH3b, '4': shipH4, '5': shipH5 },
    hit: { '2': shipH2Hit, '3a': shipH3aHit, '3b': shipH3bHit, '4': shipH4Hit, '5': shipH5Hit },
  },
};

export interface ShipSpan {
  shipId: string;
  row: number;
  col: number;
  length: number;
  vertical: boolean;
  sunk: boolean;
}

/**
 * Reconstruct ship spans from a per-cell view (the Grid deliberately only receives cells —
 * see its docblock about leak-proofing — so the skin re-derives geometry from what's
 * already visible rather than being handed placements).
 */
export function shipSpansFromCells(
  cells: { row: number; col: number; shipId: string | null; shipSunk: boolean }[],
): ShipSpan[] {
  const byShip = new Map<string, { rows: number[]; cols: number[]; sunk: boolean }>();
  for (const c of cells) {
    if (!c.shipId) continue;
    const entry = byShip.get(c.shipId) ?? { rows: [], cols: [], sunk: false };
    entry.rows.push(c.row);
    entry.cols.push(c.col);
    entry.sunk = entry.sunk || c.shipSunk;
    byShip.set(c.shipId, entry);
  }
  return [...byShip.entries()].map(([shipId, e]) => {
    const row = Math.min(...e.rows);
    const col = Math.min(...e.cols);
    const vertical = new Set(e.cols).size === 1 && e.rows.length > 1;
    return { shipId, row, col, length: e.rows.length, vertical, sunk: e.sunk };
  });
}

/** Stable 3a/3b assignment: the first length-3 ship id (sorted) is the sub, the second the
 * cruiser — purely cosmetic, but stable across renders and both players' screens. */
export function shipSpriteUrl(span: ShipSpan, allSpans: ShipSpan[]): string {
  let key: SpriteKey;
  if (span.length === 3) {
    const threes = allSpans
      .filter((s) => s.length === 3)
      .map((s) => s.shipId)
      .sort();
    key = threes.indexOf(span.shipId) === 1 ? '3b' : '3a';
  } else {
    key = String(Math.min(Math.max(span.length, 2), 5)) as SpriteKey;
  }
  return SPRITES[span.vertical ? 'v' : 'h'][span.sunk ? 'hit' : 'ok'][key];
}

/** Horizontal hull for a fleet-tracker chip, by ship id. Matches `shipSpriteUrl`'s stable
 * 3a/3b split (the sorted-first three-long id, `cruiser`, gets `3a`). */
export function fleetSpriteUrl(shipId: string, length: number, sunk: boolean): string {
  const key: SpriteKey =
    length === 3
      ? shipId === 'submarine'
        ? '3b'
        : '3a'
      : (String(Math.min(Math.max(length, 2), 5)) as SpriteKey);
  return SPRITES.h[sunk ? 'hit' : 'ok'][key];
}
